import { put, call, takeLatest, takeEvery, all } from 'redux-saga/effects';
import { error } from 'notifications/actions';
import { getAllGenerator, findGenerator, getAllPaginatedGenerator } from 'helpers/resourceSagas';
import { unpaidPurchase } from 'app/routes';
import { formatError } from 'helpers/formatters';
import * as api from './api';
import * as types from './types';
import * as actions from './actions';
import {
  setPurchase as setRenewalPurchase,
} from '../renewal/actions';
import { setUpgradePurchase as setSprintUpgradePurchase } from 'subscriptions/sprint/actions';
import { setUpgradePurchase as setBypassedUpgradePurchase } from 'subscriptions/bypassed/actions';
import { setUpgradePurchase as setQuickUpgradePurchase } from 'subscriptions/quick/actions';

export const getAll = getAllGenerator({
  resourceType: 'purchases',
  endpoint: api.getAll,
  endpointArgs: (payload) => [ payload.userId ]
});

export const fetchFiltered = getAllGenerator({
  resourceType: 'purchases',
  endpoint: api.fetchFiltered,
  endpointArgs: (payload) => [ payload.userId, payload.params ]
});

export const getHistory = getAllPaginatedGenerator({
  resourceType: 'purchases',
  endpoint: api.getHistory,
  endpointArgs: (payload) => [ payload.userId, payload.page ]
});

export function* find({ payload }) {
  try {
    yield put(actions.findPending());  
    const purchaseResponse = yield call(api.find, payload.purchaseId);

    const lineItems = purchaseResponse.data.line_items || [];
    const needToLoadMoreLineItems = lineItems.length && purchaseResponse.data.line_items_count > lineItems.length;

    if (needToLoadMoreLineItems) {
      let page = 2;
      while (page) {
        const lineItemsPageResp = yield call(api.loadLineItems, payload.purchaseId, page);
        if (lineItemsPageResp.data && lineItemsPageResp.data.results) {
          lineItems.push(...lineItemsPageResp.data.results);
          if (lineItemsPageResp.data.next) {
            page = page + 1;
          } else {
            page = null;
          }
        } 
      }
    }
    
    yield put(actions.findSucceeded(purchaseResponse));
    return purchaseResponse.data;
  }
  catch (err) {
    yield put(actions.findFailed(err));
    return {
      error: (err.response.data && err.response.data.detail) || 'Failed to load purchase',
      success: false,
    };
  }
}

export function* create({ payload }) {
  try {
    yield put(actions.createPending());  
    const resp = yield call(api.create, payload.userId);
    yield put(actions.createSucceeded(resp));
    return resp.data;
  }
  catch (err) {
    yield put(actions.createFailed(err));
    return {
      error: (err.response.data && err.response.data.detail) || 'Failed to create purchase',
      success: false,
    };
  }
}

export function* addLineItem({ payload: { purchaseId, attrs } }) {
  try {
    yield put(actions.addLineItemPending());  
    const resp = yield call(api.addLineItem, purchaseId, attrs);
    yield put(actions.addLineItemSucceeded(resp));
    return resp.data;
  }
  catch (err) {
    yield put(actions.addLineItemFailed(err));
    return {
      error: (err.response.data && err.response.data.detail) || 'Failed to add line item',
      success: false,
    };
  }
}

export function* setShippingOptions({ payload: { purchaseId, attrs } }) {
  try {
    yield put(actions.setShippingOptionsPending());  
    const resp = yield call(api.setShippingOptions, purchaseId, attrs);
    yield put(actions.setShippingOptionsSucceeded(resp));
    return resp.data;
  }
  catch (err) {
    yield put(actions.setShippingOptionsFailed(err));
    return {
      error: (err.response.data && err.response.data.detail) || 'Failed to set shipping options',
      success: false,
    };
  }
}

export function* recalculateShipping({ payload: { purchaseId } }) {
  try {
    yield put(actions.recalculateShippingPending());  
    const resp = yield call(api.recalculateShipping, purchaseId);
    yield put(actions.recalculateShippingSucceeded(resp));
    return resp.data;
  }
  catch (err) {
    yield put(actions.recalculateShippingFailed(err));
    return {
      error: (err.response.data && err.response.data.detail) || 'Failed to set shipping options',
      success: false,
    };
  }
}

export function* createRenewal({ payload: { userId } }) {
  try {
    yield put(actions.createRenewalPending());  
    const resp = yield call(api.createRenewal, userId);
    if (resp.status === 204) {
      throw new Error('No line items created');
    }
    yield put(actions.createRenewalSucceeded(resp));
    yield put(setRenewalPurchase(resp.data));
  }
  catch (err) {
    yield put(actions.createRenewalFailed());
  }
}

export function* destroy({ payload: { id } }) {
  try {
    yield put(actions.destroyPending(id));
    yield call(api.destroy, id);
    yield put(actions.destroySucceeded(id));
  }
  catch (err) {
    yield put(actions.destroyFailed(id));
  }
}

export function* createSprintUpgrade({ payload: { subId, planId } }) {
  try {
    yield put(actions.createSprintUpgradePending());
    const resp = yield call(api.createSprintUpgrade, subId, planId);
    yield put(actions.createSprintUpgradeSucceeded(resp));
    yield put(setSprintUpgradePurchase(subId, resp.data));
  }
  catch (err) {
    yield put(actions.createSprintUpgradeFailed());
  }
}

export function* createBypassedUpgrade({ payload: { subId, planId } }) {
  try {
    yield put(actions.createBypassedUpgradePending());
    const resp = yield call(api.createBypassedUpgrade, subId, planId);
    yield put(actions.createBypassedUpgradeSucceeded(resp));
    yield put(setBypassedUpgradePurchase(subId, resp.data));
  }
  catch (err) {
    yield put(actions.createBypassedUpgradeFailed());
  }
}

export function* createQuickUpgrade({ payload: { subId, planId } }) {
  try {
    yield put(actions.createQuickUpgradePending());
    const resp = yield call(api.createQuickUpgrade, subId, planId);
    yield put(actions.createQuickUpgradeSucceeded(resp));
    yield put(setQuickUpgradePurchase(subId, resp.data));
  }
  catch (err) {
    yield put(actions.createQuickUpgradeFailed());
    let errorMsg = err.response && err.response.data
      ? formatError(err.response.data)
      : 'Something went wrong. Please try again later.';
    yield put(error('upgradePurchase', errorMsg));
  }
}

export function* watchGetAll() {
  yield takeLatest(types.GET_ALL, getAll);
}

export function* watchFetchFiltered() {
  yield takeLatest(types.FETCH_FILTERED, fetchFiltered);
}

export function* watchFind() {
  yield takeEvery(types.FIND, find);
}

export function* watchCreateRenewal() {
  yield takeLatest(types.CREATE_RENEWAL, createRenewal);
}


export function* mergePurchases({ payload: { purchaseId, attrs, redirectToPurchase } }) {
  try {
    yield put(actions.mergePurchasesPending());
    const resp = yield call(api.mergePurchases, purchaseId, attrs);
    yield put(actions.mergePurchasesSucceeded(resp));
    yield put(actions.removePurchasesAfterMerge(attrs.others));
    if (redirectToPurchase) {
      yield put(unpaidPurchase(purchaseId));
    }
  }
  catch (err) {
    const { response } = err;
    yield put(actions.mergePurchasesFailed());
    yield put(error('mergePurchases', formatError(response.data)));
  }
}

export function* watchDestroy() {
  yield takeEvery(types.DESTROY, destroy);
}

export function* watchHistory() {
  yield takeLatest(types.GET_HISTORY, getHistory);
}

export function* watchCreateSprintUpgrade() {
  yield takeEvery(types.CREATE_SPRINT_UPGRADE, createSprintUpgrade);
}

export function* watchcreateBypassedUpgrade() {
  yield takeEvery(types.CREATE_BYPASSED_UPGRADE, createBypassedUpgrade);
}

export function* watchCreateQuickUpgrade() {
  yield takeEvery(types.CREATE_QUICK_UPGRADE, createQuickUpgrade);
}

export function* watchAddLineItem() {
  yield takeLatest(types.ADD_LINE_ITEM, addLineItem);
}

export function* watchRecalculateShipping() {
  yield takeLatest(types.RECALCULATE_SHIPPING, recalculateShipping);
}

export function* watchSetShippingOption() {
  yield takeLatest(types.SET_SHIPPING_OPTIONS, setShippingOptions);
}

export function* watchMergePurchases() {
  yield takeLatest(types.MERGE_PURCHASES, mergePurchases);
}

export function* watchPurchases() {
  yield all([
    call(watchGetAll),
    call(watchFetchFiltered),
    call(watchFind),
    call(watchCreateRenewal),
    call(watchDestroy),
    call(watchHistory),
    call(watchCreateSprintUpgrade),
    call(watchcreateBypassedUpgrade),
    call(watchCreateQuickUpgrade),
    call(watchAddLineItem),
    call(watchRecalculateShipping),
    call(watchSetShippingOption),
    call(watchMergePurchases),
  ]);
}
